import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Trophy, Star, Lock, Flame, Package, Zap, Wallet, Crown, Target, Shield,
  Sparkles, Moon, Award, Gem, ShoppingBag, ChevronRight, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { Gamification } from "../lib/api";
import { PageSkeleton } from "../components/PageSkeleton";

const ICON_MAP = {
  star: Star, package: Package, zap: Zap, wallet: Wallet, crown: Crown,
  target: Target, shield: Shield, sparkles: Sparkles, flame: Flame,
  moon: Moon, trophy: Trophy, award: Award,
};

const CATEGORY_META = {
  milestone: { label: "Kamienie milowe", tone: "from-purple-500 to-fuchsia-800" },
  weekly:    { label: "Tygodniowe",       tone: "from-amber-500 to-orange-700" },
  cod:       { label: "Pobrania",         tone: "from-emerald-500 to-emerald-800" },
  routes:    { label: "Trasy",            tone: "from-sky-500 to-indigo-800" },
  quality:   { label: "Jakość",           tone: "from-red-500 to-rose-800" },
  streak:    { label: "Passa",            tone: "from-orange-500 to-red-800" },
  special:   { label: "Specjalne",        tone: "from-zinc-600 to-zinc-950" },
  custom:    { label: "Inne",             tone: "from-zinc-500 to-zinc-800" },
};

export default function AchievementsPage() {
  const [data, setData] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | unlocked | locked

  useEffect(() => {
    Promise.all([Gamification.achievements(), Gamification.me()])
      .then(([a, m]) => { setData(a); setMe(m); })
      .catch(() => toast.error("Nie mogę pobrać osiągnięć"))
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    if (!data?.achievements) return {};
    const src = data.achievements.filter((a) => {
      if (filter === "unlocked") return a.unlocked;
      if (filter === "locked") return !a.unlocked;
      return true;
    });
    return src.reduce((acc, a) => {
      const key = a.category || "custom";
      (acc[key] ||= []).push(a);
      return acc;
    }, {});
  }, [data, filter]);

  const totals = useMemo(() => {
    if (!data?.achievements) return { total: 0, unlocked: 0 };
    return {
      total: data.achievements.length,
      unlocked: data.achievements.filter((a) => a.unlocked).length,
    };
  }, [data]);

  if (loading) {
    return (
      <PageSkeleton label="Ładuję osiągnięcia…" />
    );
  }

  return (
    <div className="p-3 md:p-6 space-y-5" data-testid="achievements-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-600 via-fuchsia-700 to-zinc-950 text-white p-6 md:p-8 ring-1 ring-white/10 shadow-[0_25px_60px_-25px_rgba(0,0,0,0.7)]">
        <Trophy className="absolute -right-10 -bottom-16 w-72 h-72 opacity-[0.10]" strokeWidth={1.5} />
        <div className="relative flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center ring-1 ring-white/25">
            <Trophy className="w-5 h-5" />
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/70">
            // Osiągnięcia GoPossible
          </div>
        </div>
        <h1 className="relative mt-3 font-heading font-black text-3xl md:text-5xl uppercase tracking-tight">
          {totals.unlocked} <span className="text-white/40">/</span> {totals.total}
        </h1>
        <div className="relative mt-1 text-white/80">
          Zdobyte odznaki. Odblokuj je robiąc paczki, zbierając pobrania i kończąc trasy.
        </div>

        <div className="relative mt-5 flex items-center gap-3 flex-wrap">
          <StatChip icon={Gem} label="Diamenty" value={me?.diamonds ?? 0} tone="cyan" testId="ach-diamonds" />
          <StatChip icon={Zap} label="Bonus XP" value={me?.bonus_xp ?? 0} tone="amber" testId="ach-bonusxp" />
          <Link
            to="/sklep"
            data-testid="btn-goto-shop"
            className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-purple-900 font-bold uppercase text-xs tracking-widest hover:bg-fuchsia-100 transition-colors"
          >
            <ShoppingBag className="w-4 h-4" /> Sklep GoPossible
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Filter chips */}
        <div className="relative mt-5 flex gap-2 flex-wrap">
          {[
            { id: "all", label: `Wszystkie (${totals.total})` },
            { id: "unlocked", label: `Zdobyte (${totals.unlocked})` },
            { id: "locked", label: `Do zdobycia (${totals.total - totals.unlocked})` },
          ].map((f) => (
            <button
              key={f.id}
              data-testid={`ach-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest transition-colors ${
                filter === f.id
                  ? "bg-white text-purple-900"
                  : "bg-white/10 text-white/70 hover:bg-white/20"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Groups */}
      <div className="space-y-6">
        {Object.entries(grouped).map(([cat, list]) => {
          const meta = CATEGORY_META[cat] || CATEGORY_META.custom;
          return (
            <section key={cat} data-testid={`ach-category-${cat}`}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`h-2 w-6 rounded-full bg-gradient-to-r ${meta.tone}`} />
                <h2 className="font-heading font-black uppercase tracking-tight text-lg text-zinc-900">
                  {meta.label}
                </h2>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 ml-auto">
                  {list.filter((a) => a.unlocked).length} / {list.length}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {list.map((a) => <AchievementCard key={a.code} ach={a} categoryTone={meta.tone} />)}
              </div>
            </section>
          );
        })}
        {Object.keys(grouped).length === 0 && (
          <div className="p-12 text-center text-zinc-500 text-sm">
            Brak osiągnięć w wybranym filtrze.
          </div>
        )}
      </div>
    </div>
  );
}

function AchievementCard({ ach, categoryTone }) {
  const Icon = ICON_MAP[ach.icon] || Star;
  const pct = ach.progress?.percent ?? 0;
  const unlocked = ach.unlocked;
  return (
    <div
      data-testid={`ach-card-${ach.code}`}
      className={`relative overflow-hidden rounded-2xl p-4 min-h-[160px] ring-1 transition-all ${
        unlocked
          ? `bg-gradient-to-br ${categoryTone} text-white ring-white/15 shadow-[0_15px_35px_-15px_rgba(0,0,0,0.65)]`
          : "bg-white text-zinc-800 ring-zinc-200 hover:ring-zinc-400"
      }`}
    >
      <Icon
        aria-hidden
        className={`absolute -right-6 -bottom-6 w-32 h-32 ${unlocked ? "opacity-[0.16]" : "opacity-[0.06]"}`}
        strokeWidth={1.5}
      />
      <div className="relative flex items-start justify-between gap-2">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ring-1 ${unlocked ? "bg-white/20 ring-white/25" : "bg-zinc-100 ring-zinc-200"}`}>
          <Icon className="w-4 h-4" />
        </div>
        {unlocked ? (
          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white text-emerald-700 text-[10px] font-mono uppercase tracking-widest">
            <Star className="w-3 h-3" /> Zdobyte
          </div>
        ) : (
          <Lock className="w-4 h-4 text-zinc-400" />
        )}
      </div>

      <div className="relative mt-3">
        <div className={`font-heading font-black text-lg uppercase tracking-tight ${unlocked ? "" : "text-zinc-900"}`}>
          {ach.name}
        </div>
        <div className={`text-xs mt-1 ${unlocked ? "text-white/85" : "text-zinc-500"}`}>
          {ach.description}
        </div>
      </div>

      {/* Rewards */}
      <div className="relative mt-3 flex items-center gap-2 flex-wrap">
        {ach.diamond_reward > 0 && (
          <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-widest ${
            unlocked ? "bg-white/15 text-cyan-100 ring-1 ring-white/20" : "bg-cyan-50 text-cyan-700 ring-1 ring-cyan-200"
          }`}>
            <Gem className="w-3 h-3" /> {ach.diamond_reward}
          </div>
        )}
        {ach.xp_reward > 0 && (
          <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-widest ${
            unlocked ? "bg-white/15 text-amber-100 ring-1 ring-white/20" : "bg-amber-50 text-amber-800 ring-1 ring-amber-200"
          }`}>
            <Zap className="w-3 h-3" /> {ach.xp_reward} XP
          </div>
        )}
      </div>

      {/* Progress bar (locked only) */}
      {!unlocked && ach.condition_type !== "manual" && (
        <div className="relative mt-3">
          <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden">
            <div className={`h-full bg-gradient-to-r ${categoryTone}`} style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-1 text-[10px] font-mono text-zinc-500">
            {ach.progress?.current ?? 0} / {ach.progress?.target ?? 0} ({pct}%)
          </div>
        </div>
      )}
      {!unlocked && ach.condition_type === "manual" && (
        <div className="relative mt-3 text-[10px] font-mono text-zinc-500">
          Przyznaje administrator
        </div>
      )}
    </div>
  );
}

function StatChip({ icon: Icon, label, value, tone = "cyan", testId }) {
  const toneMap = {
    cyan: "bg-cyan-500/20 text-cyan-100 ring-cyan-300/40",
    amber: "bg-amber-500/20 text-amber-100 ring-amber-300/40",
  };
  return (
    <div data-testid={testId} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ring-1 ${toneMap[tone]}`}>
      <Icon className="w-4 h-4" />
      <span className="text-[10px] font-mono uppercase tracking-widest">{label}</span>
      <span className="font-heading font-black text-base">{Number(value).toLocaleString("pl-PL")}</span>
    </div>
  );
}
